import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { CalendarOff, Plus, Loader2, CheckCircle, XCircle } from "lucide-react";

const TimeOff = () => {
  const { user, canManage, getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [filter, setFilter] = useState("all");

  const [form, setForm] = useState({
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: "",
    type: "vacation",
    reason: "",
  });

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/time-off-requests${params}`, { headers: getAuthHeaders() });
      setRequests(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.end_date) { toast.error("Please set an end date"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/time-off-requests`, form, { headers: getAuthHeaders() });
      toast.success("Time-off request submitted");
      setShowCreate(false);
      setForm({ start_date: format(new Date(), "yyyy-MM-dd"), end_date: "", type: "vacation", reason: "" });
      fetchRequests();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit request");
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessingId(id);
    try {
      const res = await axios.put(`${API}/time-off-requests/${id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success(`Approved. ${res.data.shifts_unassigned} shift(s) unassigned.`);
      fetchRequests();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to approve");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id) => {
    setProcessingId(id);
    try {
      await axios.put(`${API}/time-off-requests/${id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("Request rejected");
      fetchRequests();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reject");
    } finally {
      setProcessingId(null);
    }
  };

  const typeColors = {
    vacation: "bg-blue-100 text-blue-800",
    sick: "bg-red-100 text-red-800",
    personal: "bg-purple-100 text-purple-800",
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="time-off-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Time Off</h1>
          <p className="text-[#666666]">{canManage ? "Manage employee time-off requests" : "Request and view your time off"}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#6A1E2E] hover:bg-[#521422] text-white" data-testid="request-time-off-btn">
          <Plus className="w-4 h-4 mr-2" /> Request Time Off
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {["all", "pending", "approved", "rejected"].map(f => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? "bg-[#6A1E2E] hover:bg-[#521422] text-white" : "border-[#E5E5E5]"}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-pulse text-[#666666]">Loading...</div></div>
      ) : requests.length === 0 ? (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardContent className="py-12 text-center">
            <CalendarOff className="w-12 h-12 mx-auto text-[#CCCCCC] mb-3" />
            <p className="text-[#666666]">No time-off requests found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {canManage && <TableHead>Employee</TableHead>}
                <TableHead>Type</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map(req => (
                <TableRow key={req.id} data-testid={`timeoff-row-${req.id}`}>
                  {canManage && <TableCell className="font-medium">{req.user_name || "—"}</TableCell>}
                  <TableCell>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-sm capitalize ${typeColors[req.type] || ""}`}>{req.type}</span>
                  </TableCell>
                  <TableCell>{req.start_date}</TableCell>
                  <TableCell>{req.end_date}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{req.reason || "—"}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-sm capitalize ${statusColors[req.status] || ""}`}>{req.status}</span>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      {req.status === "pending" && (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(req.id)}
                            disabled={processingId === req.id}
                            className="bg-green-600 hover:bg-green-700 text-white h-8"
                            data-testid={`approve-timeoff-${req.id}`}
                          >
                            {processingId === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(req.id)}
                            disabled={processingId === req.id}
                            className="border-red-300 text-red-600 hover:bg-red-50 h-8"
                            data-testid={`reject-timeoff-${req.id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-[#6A1E2E]" /> Request Time Off
            </DialogTitle>
            <DialogDescription>Submit a time-off request for approval.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2" data-testid="create-timeoff-form">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="timeoff-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="sick">Sick</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} required className="border-[#E5E5E5]" data-testid="timeoff-start" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} required className="border-[#E5E5E5]" data-testid="timeoff-end" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="Brief reason..." className="border-[#E5E5E5] resize-none" rows={3} data-testid="timeoff-reason" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="border-[#E5E5E5]">Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-[#6A1E2E] hover:bg-[#521422] text-white" data-testid="submit-timeoff">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeOff;
